import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Download, Filter, RotateCcw } from 'lucide-react'
import type { ReactNode } from 'react'
import { z } from 'zod'
import { resolveAppLanguageCode, useTranslation } from '@/lib/i18n'
import { loadRequestLogs } from './-lib/logs-loader'

const searchSchema = z.object({
	apiKeyId: z.string().optional(),
	route: z.string().optional(),
	method: z.string().optional(),
	statusClass: z.enum(['2xx', '3xx', '4xx', '5xx']).optional(),
	apiKeyStatus: z.enum(['active', 'expired', 'revoked']).optional(),
	from: z.string().optional(),
	to: z.string().optional(),
	cursor: z.string().optional()
})

export const Route = createFileRoute('/(private)/logs')({
	ssr: false,
	validateSearch: searchSchema,
	loaderDeps: ({ search }) => search,
	loader: ({ abortController, deps }) =>
		loadRequestLogs({ search: deps, signal: abortController.signal }),
	component: LogsPage
})

function LogsPage() {
	const data = Route.useLoaderData()
	const search = Route.useSearch()
	const navigate = useNavigate({ from: Route.fullPath })
	const { t, i18n } = useTranslation()
	const locale = resolveAppLanguageCode(
		i18n.resolvedLanguage ?? i18n.language
	)
	const numberFormatter = new Intl.NumberFormat(locale)

	async function updateSearch(next: Partial<typeof search>) {
		await navigate({ search: { ...search, ...next, cursor: undefined } })
	}

	async function loadNextPage() {
		await navigate({
			search: { ...search, cursor: data.meta.nextCursor ?? undefined }
		})
	}

	async function resetFilters() {
		await navigate({ search: {} })
	}

	const hasFilters = Boolean(
		search.apiKeyId ||
			search.apiKeyStatus ||
			search.method ||
			search.route ||
			search.statusClass
	)

	function exportCsv() {
		const rows = [
			[
				'createdAt',
				'method',
				'path',
				'routePattern',
				'statusCode',
				'durationMs',
				'apiKey',
				'requestId',
				'errorCode'
			],
			...data.logs.map((log) => [
				log.createdAt,
				log.method,
				log.path,
				log.routePattern,
				String(log.statusCode ?? ''),
				String(log.durationMs ?? ''),
				log.apiKey.label,
				log.requestId,
				log.errorCode ?? ''
			])
		]
		const csv = rows.map((row) => row.map(csvValue).join(',')).join('\n')
		const url = URL.createObjectURL(
			new Blob([csv], { type: 'text/csv;charset=utf-8' })
		)
		const link = document.createElement('a')
		link.href = url
		link.download = t('common:logs.export_filename')
		link.click()
		URL.revokeObjectURL(url)
	}

	return (
		<main className='portal-container simple-page py-9 pb-20'>
			<div className='dashboard-title'>
				<div>
					<p className='portal-eyebrow'>{t('common:logs.eyebrow')}</p>
					<h1>{t('common:logs.title')}</h1>
					<p className='portal-lead'>{t('common:logs.subtitle')}</p>
				</div>
				<button
					className='portal-button portal-button--secondary'
					onClick={exportCsv}
					type='button'
				>
					<Download aria-hidden='true' />
					{t('common:logs.export_csv')}
				</button>
			</div>
			<section
				aria-label={t('common:logs.filters_label')}
				className='logs-filters'
			>
				<div className='logs-filters__header'>
					<div>
						<span className='logs-filters__eyebrow'>
							<Filter aria-hidden='true' />
							{t('common:logs.filters_eyebrow')}
						</span>
						<p>{t('common:logs.filters_description')}</p>
					</div>
					{hasFilters ? (
						<button
							className='portal-button portal-button--ghost portal-button--small'
							onClick={resetFilters}
							type='button'
						>
							<RotateCcw aria-hidden='true' />
							{t('common:logs.reset_filters')}
						</button>
					) : null}
				</div>
				<div className='logs-filters__grid'>
					<FilterField label={t('common:logs.status_label')}>
						<select
							aria-label={t('common:logs.status_label')}
							onChange={(event) =>
								updateSearch({
									statusClass: getStatusClass(
										event.target.value
									)
								})
							}
							value={search.statusClass ?? ''}
						>
							<option value=''>
								{t('common:logs.all_statuses')}
							</option>
							<option value='2xx'>2xx</option>
							<option value='3xx'>3xx</option>
							<option value='4xx'>4xx</option>
							<option value='5xx'>5xx</option>
						</select>
					</FilterField>
					<FilterField label={t('common:logs.method_label')}>
						<select
							aria-label={t('common:logs.method_label')}
							onChange={(event) =>
								updateSearch({
									method: event.target.value || undefined
								})
							}
							value={search.method ?? ''}
						>
							<option value=''>
								{t('common:logs.all_methods')}
							</option>
							<option value='GET'>GET</option>
							<option value='POST'>POST</option>
							<option value='PATCH'>PATCH</option>
						</select>
					</FilterField>
					<FilterField label={t('common:logs.api_key_label')}>
						<select
							aria-label={t('common:logs.api_key_label')}
							onChange={(event) =>
								updateSearch({
									apiKeyId: event.target.value || undefined
								})
							}
							value={search.apiKeyId ?? ''}
						>
							<option value=''>
								{t('common:logs.all_api_keys')}
							</option>
							{data.keys.map((key) => (
								<option key={key.id} value={key.id}>
									{key.label}
								</option>
							))}
						</select>
					</FilterField>
					<FilterField label={t('common:logs.api_key_status_label')}>
						<select
							aria-label={t('common:logs.api_key_status_label')}
							onChange={(event) =>
								updateSearch({
									apiKeyStatus: getApiKeyStatus(
										event.target.value
									)
								})
							}
							value={search.apiKeyStatus ?? ''}
						>
							<option value=''>
								{t('common:logs.all_api_key_statuses')}
							</option>
							<option value='active'>
								{t('common:logs.api_key_status_active')}
							</option>
							<option value='revoked'>
								{t('common:logs.api_key_status_revoked')}
							</option>
							<option value='expired'>
								{t('common:logs.api_key_status_expired')}
							</option>
						</select>
					</FilterField>
					<FilterField
						className='logs-filters__route'
						label={t('common:logs.route_label')}
					>
						<input
							aria-label={t('common:logs.route_label')}
							onChange={(event) =>
								updateSearch({
									route: event.target.value || undefined
								})
							}
							placeholder='/v1/cards'
							value={search.route ?? ''}
						/>
					</FilterField>
				</div>
			</section>
			<span className='portal-meta'>
				{t('common:logs.requests', {
					count: data.meta.total,
					formattedCount: numberFormatter.format(data.meta.total)
				})}
			</span>
			<section className='overflow-x-auto'>
				<table className='portal-table w-full'>
					<thead>
						<tr>
							<th>{t('common:logs.time')}</th>
							<th>{t('common:logs.method')}</th>
							<th>{t('common:logs.endpoint')}</th>
							<th>{t('common:logs.status')}</th>
							<th>{t('common:logs.duration')}</th>
							<th>{t('common:logs.api_key')}</th>
						</tr>
					</thead>
					<tbody>
						{data.logs.length === 0 ? (
							<tr>
								<td colSpan={6}>{t('common:logs.empty')}</td>
							</tr>
						) : (
							data.logs.map((log) => (
								<tr key={log.id}>
									<td>{formatTime(log.createdAt, locale)}</td>
									<td>
										<span className='method-pill'>
											{log.method}
										</span>
									</td>
									<td>{log.path}</td>
									<td>
										<span
											className={
												isSuccessful(log.statusCode)
													? 'status-pill status-pill--ok'
													: 'status-pill status-pill--warning'
											}
										>
											{log.statusCode ??
												t('common:logs.not_available')}
										</span>
									</td>
									<td>
										{log.durationMs === null
											? t('common:logs.not_available')
											: t('common:logs.duration_value', {
													value: numberFormatter.format(
														log.durationMs
													)
												})}
									</td>
									<td>{log.apiKey.label}</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</section>
			{data.meta.nextCursor ? (
				<button
					className='portal-button portal-button--secondary portal-button--small'
					onClick={loadNextPage}
					type='button'
				>
					{t('common:logs.load_more')}
				</button>
			) : null}
		</main>
	)
}

function FilterField(props: {
	children: ReactNode
	className?: string
	label: string
}) {
	return (
		<div className={`logs-filters__field ${props.className ?? ''}`}>
			<span>{props.label}</span>
			{props.children}
		</div>
	)
}

function csvValue(value: string) {
	return `"${value.replaceAll('"', '""')}"`
}
function formatTime(value: string, locale: string) {
	return new Intl.DateTimeFormat(locale, {
		dateStyle: 'short',
		timeStyle: 'medium'
	}).format(new Date(value))
}
function isSuccessful(status: number | null) {
	return status !== null && status >= 200 && status < 400
}
function getStatusClass(value: string) {
	return value === '2xx' ||
		value === '3xx' ||
		value === '4xx' ||
		value === '5xx'
		? value
		: undefined
}

function getApiKeyStatus(value: string) {
	return value === 'active' || value === 'expired' || value === 'revoked'
		? value
		: undefined
}
